import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, ButtonGroup, Input, Table } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatCurrency } from "../../../../_helpers/numberFormatter";
import bidItemActions from "../../../../_actions/bidItemActions";
import { docPermConstants } from "../../../../_constants/permissionConstants";
import "./projAdminItems.css";

export default function DivisionOfWorkTab({ projectId, permissions }) {
  const dispatch = useDispatch();
  const projItemList = useSelector(
    (state) => state.bidItems.projItemLists[projectId] || []
  );
  const updating = useSelector((state) => state.bidItems.updating || false);

  const [editedItems, setEditedItems] = useState({});
  const [rawValues, setRawValues] = useState({});
  const [filter, setFilter] = useState("all");
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    dispatch(bidItemActions.getAll(projectId));
  }, [projectId, dispatch]);

  // If a structure qty was already assigned by location in the Items tab
  // ("Edit Location Quantities..."), that takes priority over this tab's
  // value and must not be edited here to avoid the two features clashing.
  const isLocationLocked = (item) => (item.totalStructureQuantity || 0) > 0;
  const getBaselineQty = (item) =>
    isLocationLocked(item) ? item.totalStructureQuantity : item.dowStructureQuantity || 0;

  useEffect(() => {
    const initialEdits = {};
    projItemList.forEach((item) => {
      initialEdits[item.bidItemId] = getBaselineQty(item);
    });
    setEditedItems(initialEdits);
  }, [projItemList]);

  const filteredItems = useMemo(() => {
    let items = projItemList;

    if (searchText) {
      const search = searchText.toLowerCase();
      items = items.filter(
        (i) =>
          i.description.toLowerCase().includes(search) ||
          i.itemNo.toString().includes(search)
      );
    }

    if (filter === "structures") {
      items = items.filter((i) => (editedItems[i.bidItemId] || 0) > 0);
    } else if (filter === "roadway") {
      items = items.filter((i) => (editedItems[i.bidItemId] || 0) === 0);
    }

    // Sort by item number
    items = [...items].sort((a, b) => a.itemNo - b.itemNo);

    return items;
  }, [projItemList, filter, searchText, editedItems]);

  const handleQuantityChange = useCallback((bidItemId, value) => {
    setRawValues((prev) => ({ ...prev, [bidItemId]: value }));
  }, []);

  const handleQuantityBlur = useCallback((bidItemId, value, totalQty) => {
    const numValue = Math.min(totalQty, Math.max(0, parseFloat(value) || 0));
    setEditedItems((prev) => ({ ...prev, [bidItemId]: numValue }));
    setRawValues((prev) => {
      const next = { ...prev };
      delete next[bidItemId];
      return next;
    });
  }, []);

  const hasChanges = useMemo(() => {
    return projItemList.some(
      (item) => !isLocationLocked(item) && editedItems[item.bidItemId] !== getBaselineQty(item)
    );
  }, [projItemList, editedItems]);

  const canEdit = permissions.includes(docPermConstants.EDIT_LEVEL_2);

  const handleSelectRecommended = useCallback(() => {
    setEditedItems((prev) => {
      const updated = { ...prev };
      projItemList.forEach((item) => {
        if (isLocationLocked(item)) return;
        const codeWithoutLeadingZeros = item.code?.replace(/^0+/, "") || "";
        const codePrefix = parseInt(codeWithoutLeadingZeros.substring(0, 2), 10);
        const isStructureCode = codePrefix >= 45 && codePrefix <= 60;
        if (isStructureCode) {
          updated[item.bidItemId] = item.eeQuantity + (item.ccoQuantity || 0);
        }
      });
      return updated;
    });
  }, [projItemList]);

  const handleUnselectAll = useCallback(() => {
    setEditedItems((prev) => {
      const updated = { ...prev };
      projItemList.forEach((item) => {
        if (isLocationLocked(item)) return;
        updated[item.bidItemId] = 0;
      });
      return updated;
    });
  }, [projItemList]);

  // Helper to identify MOB and TRO items
  const isMobItem = useCallback((item) => {
    const code = item.code?.replace(/^0+/, "") || "";
    return code === "999990";
  }, []);

  const isTroItem = useCallback((item) => {
    const code = item.code?.replace(/^0+/, "") || "";
    return code === "90105" || code === "90100";
  }, []);

  const isMobOrTroItem = useCallback((item) => {
    return isMobItem(item) || isTroItem(item);
  }, [isMobItem, isTroItem]);

  // Check if MOB or TRO items exist
  const { mobItem, troItem, hasMobOrTro } = useMemo(() => {
    const mob = projItemList.find((item) => isMobItem(item));
    const tro = projItemList.find((item) => isTroItem(item));
    return {
      mobItem: mob,
      troItem: tro,
      hasMobOrTro: !!(mob || tro),
    };
  }, [projItemList, isMobItem, isTroItem]);

  const handleSave = useCallback(async () => {
    let updatedItems = { ...editedItems };

    if (hasMobOrTro) {
      let totalExcludingMobTro = 0;
      let structuresExcludingMobTro = 0;

      projItemList.forEach((item) => {
        if (isMobOrTroItem(item)) return;
        const itemTotalQty = item.eeQuantity + (item.ccoQuantity || 0);
        const structQty = editedItems[item.bidItemId] || 0;
        const bidAmount = item.eeQuantity * item.bidUnitPrice;
        totalExcludingMobTro += bidAmount;
        if (itemTotalQty > 0) {
          structuresExcludingMobTro += bidAmount * (structQty / itemTotalQty);
        }
      });

      const structurePercent = totalExcludingMobTro > 0
        ? structuresExcludingMobTro / totalExcludingMobTro
        : 0;

      if (mobItem && !isLocationLocked(mobItem)) {
        const mobTotalQty = mobItem.eeQuantity + (mobItem.ccoQuantity || 0);
        updatedItems[mobItem.bidItemId] = parseFloat((mobTotalQty * structurePercent).toFixed(2));
      }

      if (troItem && !isLocationLocked(troItem)) {
        const troTotalQty = troItem.eeQuantity + (troItem.ccoQuantity || 0);
        updatedItems[troItem.bidItemId] = parseFloat((troTotalQty * structurePercent).toFixed(2));
      }

      setEditedItems(updatedItems);
    }

    const changedItems = projItemList
      .filter((item) => !isLocationLocked(item) && updatedItems[item.bidItemId] !== getBaselineQty(item))
      .map((item) => ({
        bidItemId: item.bidItemId,
        structureQuantity: updatedItems[item.bidItemId],
      }));

    if (changedItems.length === 0) return;

    const result = await dispatch(
      bidItemActions.bulkUpdateDivisionOfWork(projectId, changedItems)
    );
    if (result) {
      dispatch(bidItemActions.getAll(projectId));
    }
  }, [projItemList, editedItems, dispatch, projectId, hasMobOrTro, mobItem, troItem, isMobOrTroItem]);

  const summary = useMemo(() => {
    let totalBidAmount = 0;
    let structuresAmount = 0;
    let totalQuantity = 0;
    let structuresQuantity = 0;

    // Amounts excluding MOB & TRO
    let totalExcludingMobTro = 0;
    let structuresExcludingMobTro = 0;

    // MOB & TRO amounts
    let totalMobTro = 0;
    let structuresMobTro = 0;

    projItemList.forEach((item) => {
      const itemTotalQty = item.eeQuantity + (item.ccoQuantity || 0);
      const structQty = editedItems[item.bidItemId] || 0;
      const bidAmount = item.eeQuantity * item.bidUnitPrice;
      const isMobTro = isMobOrTroItem(item);

      totalQuantity += itemTotalQty;
      structuresQuantity += structQty;
      totalBidAmount += bidAmount;

      // Calculate structure amount based on quantity ratio
      let itemStructAmount = 0;
      if (itemTotalQty > 0) {
        itemStructAmount = bidAmount * (structQty / itemTotalQty);
        structuresAmount += itemStructAmount;
      }

      // Separate MOB & TRO from regular items
      if (isMobTro) {
        totalMobTro += bidAmount;
        structuresMobTro += itemStructAmount;
      } else {
        totalExcludingMobTro += bidAmount;
        structuresExcludingMobTro += itemStructAmount;
      }
    });

    const structuresPercentExcludingMobTro = totalExcludingMobTro > 0
      ? (structuresExcludingMobTro / totalExcludingMobTro) * 100
      : 0;

    const structuresPercentWithMobTro = totalBidAmount > 0
      ? (structuresAmount / totalBidAmount) * 100
      : 0;

    return {
      // Totals with everything
      totalBidAmount,
      structuresAmount,
      roadwayAmount: totalBidAmount - structuresAmount,
      structuresPercent: Math.round(structuresPercentWithMobTro),

      // Totals excluding MOB & TRO
      totalExcludingMobTro,
      structuresExcludingMobTro,
      roadwayExcludingMobTro: totalExcludingMobTro - structuresExcludingMobTro,
      structuresPercentExcludingMobTro: Math.round(structuresPercentExcludingMobTro),

      // MOB & TRO only
      totalMobTro,
      structuresMobTro,
      roadwayMobTro: totalMobTro - structuresMobTro,

      // Quantities
      totalQuantity,
      structuresQuantity,
      roadwayQuantity: totalQuantity - structuresQuantity,
    };
  }, [projItemList, editedItems, isMobOrTroItem]);

  return (
    <div className="p-2">
      {/* Summary Bar */}
      {hasMobOrTro ? (
        <div className="mb-2 bg-lightgray p-2 border rounded">
          <div className="d-flex" style={{ fontWeight: "bold", borderBottom: "1px solid #ccc", paddingBottom: "4px", marginBottom: "4px" }}>
            <div style={{ flex: "1 1 200px" }}></div>
            <div style={{ flex: "0 0 120px" }} className="text-right">Structures</div>
            <div style={{ flex: "0 0 120px" }} className="text-right">Roadway</div>
            <div style={{ flex: "0 0 120px" }} className="text-right">Total</div>
          </div>
          <div className="d-flex" style={{ paddingBottom: "2px" }}>
            <div style={{ flex: "1 1 200px" }}><strong>Not Including MOB & TRO</strong></div>
            <div style={{ flex: "0 0 120px" }} className="text-right">{formatCurrency(summary.structuresExcludingMobTro)}</div>
            <div style={{ flex: "0 0 120px" }} className="text-right">{formatCurrency(summary.roadwayExcludingMobTro)}</div>
            <div style={{ flex: "0 0 120px" }} className="text-right">{formatCurrency(summary.totalExcludingMobTro)}</div>
          </div>
          <div className="d-flex" style={{ paddingBottom: "2px" }}>
            <div style={{ flex: "1 1 200px" }}><strong>MOB & TRO</strong> (Automatically Calculated on Save)</div>
            <div style={{ flex: "0 0 120px" }} className="text-right">{formatCurrency(summary.structuresMobTro)}</div>
            <div style={{ flex: "0 0 120px" }} className="text-right">{formatCurrency(summary.roadwayMobTro)}</div>
            <div style={{ flex: "0 0 120px" }} className="text-right">{formatCurrency(summary.totalMobTro)}</div>
          </div>
          <div className="d-flex" style={{ borderTop: "1px solid #ccc", paddingTop: "4px", marginTop: "2px" }}>
            <div style={{ flex: "1 1 200px" }}><strong>Total</strong></div>
            <div style={{ flex: "0 0 120px" }} className="text-right"><strong>{formatCurrency(summary.structuresAmount)}</strong></div>
            <div style={{ flex: "0 0 120px" }} className="text-right"><strong>{formatCurrency(summary.roadwayAmount)}</strong></div>
            <div style={{ flex: "0 0 120px" }} className="text-right"><strong>{formatCurrency(summary.totalBidAmount)}</strong></div>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-row flex-wrap mb-2">
          <div className="mr-4">
            <strong>Total Contract:</strong> {formatCurrency(summary.totalBidAmount)}
          </div>
          <div className="mr-4">
            <strong>Total Structures:</strong> {formatCurrency(summary.structuresAmount)} (
            {summary.structuresPercent}%)
          </div>
          <div className="mr-4">
            <strong>Total Roadway:</strong> {formatCurrency(summary.roadwayAmount)} (
            {100 - summary.structuresPercent}%)
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="d-flex flex-row flex-wrap mb-2 align-items-center">
        <ButtonGroup className="mr-2">
          <Button
            color="success"
            outline
            active={filter === "all"}
            onClick={() => setFilter("all")}
          >
            All
          </Button>
          <Button
            color="success"
            outline
            active={filter === "roadway"}
            onClick={() => setFilter("roadway")}
          >
            Roadway
          </Button>
          <Button
            color="success"
            outline
            active={filter === "structures"}
            onClick={() => setFilter("structures")}
          >
            Structures
          </Button>
        </ButtonGroup>

        <div className="input-group mr-2" style={{ maxWidth: "300px" }}>
          <div className="input-group-prepend">
            <span className="input-group-text">
              <FontAwesomeIcon icon="search" />
            </span>
          </div>
          <Input
            placeholder="Search by # or description"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div className="ml-auto">
          <Button
            color="secondary"
            outline
            className="mr-2"
            disabled={!canEdit}
            onClick={handleUnselectAll}
          >
            Unselect All
          </Button>
          <Button
            color="secondary"
            outline
            className="mr-2"
            disabled={!canEdit}
            onClick={handleSelectRecommended}
          >
            Select Structures Code
          </Button>
          <Button
            color="primary"
            disabled={!hasChanges || updating || !canEdit}
            onClick={handleSave}
          >
            {updating ? (
              <FontAwesomeIcon icon="spinner" spin className="mr-1" />
            ) : (
              <FontAwesomeIcon icon="save" className="mr-1" />
            )}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Items Table */}
      <Table bordered hover size="sm">
        <thead className="bg-lightgray">
          <tr>
            <th className="p-1" style={{ width: "60px" }}>No.</th>
            <th className="p-1" style={{ width: "70px" }}>Code</th>
            <th className="p-1">Description</th>
            <th className="p-1 text-right" style={{ width: "120px" }}>
              Bid Amount
            </th>
            <th className="p-1 text-center" style={{ width: "60px" }}>
              Str?
            </th>
            <th className="p-1 text-right" style={{ width: "90px" }}>
              Str. Qty
            </th>
            <th className="p-1 text-right" style={{ width: "90px" }}>
              Rdwy Qty
            </th>
            <th className="p-1 text-right" style={{ width: "100px" }}>
              Total Qty
            </th>
            <th className="p-1" style={{ width: "60px" }}>
              Unit
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredItems.map((item) => {
            const totalQty = item.eeQuantity + (item.ccoQuantity || 0);
            const structQty = editedItems[item.bidItemId] || 0;
            const rdwyQty = Math.max(0, totalQty - structQty);
            const bidAmount = item.eeQuantity * item.bidUnitPrice;
            const locked = isLocationLocked(item);
            const isChanged = !locked && structQty !== getBaselineQty(item);
            const codeWithoutLeadingZeros = item.code?.replace(/^0+/, "") || "";
            const codePrefix = parseInt(codeWithoutLeadingZeros.substring(0, 2), 10);
            const isStructureCode = codePrefix >= 45 && codePrefix <= 60;
            const lockedTitle =
              "Structure quantity was assigned by location in the Items tab; edit it there.";

            return (
              <tr
                key={item.bidItemId}
                className={structQty > 0 ? "" : isChanged ? "table-warning" : ""}
                style={structQty > 0 ? { backgroundColor: "#cee6ff" } : {}}
              >
                <td className="p-1">#{item.itemNo}</td>
                <td className="p-1" style={isStructureCode ? { backgroundColor: "#cee6ff" } : {}}>{item.code}</td>
                <td className="p-1">{item.description}</td>
                <td className="p-1 text-right">{formatCurrency(bidAmount)}</td>
                <td className="p-1 text-center align-middle">
                  <Input
                    type="checkbox"
                    checked={structQty > 0}
                    onChange={(e) =>
                      handleQuantityChange(item.bidItemId, e.target.checked ? totalQty : 0, totalQty)
                    }
                    disabled={!canEdit || locked}
                    title={locked ? lockedTitle : undefined}
                    className="m-0 position-static"
                    style={{ marginLeft: "auto", marginRight: "auto" }}
                  />
                </td>
                <td className="p-1 text-right">
                  <Input
                    type="number"
                    bsSize="sm"
                    min={0}
                    max={totalQty}
                    step="0.001"
                    value={item.bidItemId in rawValues ? rawValues[item.bidItemId] : structQty}
                    onChange={(e) => handleQuantityChange(item.bidItemId, e.target.value)}
                    onBlur={(e) => handleQuantityBlur(item.bidItemId, e.target.value, totalQty)}
                    disabled={!canEdit || locked}
                    title={locked ? lockedTitle : undefined}
                    style={{ width: "70px", display: "inline" }}
                  />
                </td>
                <td className="p-1 text-right text-muted">{parseFloat(rdwyQty.toFixed(3))}</td>
                <td className="p-1 text-right">{totalQty}</td>
                <td className="p-1">{item.unitOfMeasure}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {filteredItems.length === 0 && (
        <div className="text-center text-muted p-4">
          No items found matching the current filter.
        </div>
      )}

      {/* Scroll to Top Button */}
      <div className="text-center mt-3 mb-2">
        <Button
          color="link"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          title="Back to top"
        >
          <FontAwesomeIcon icon="arrow-up" className="mr-1" />
          Back to Top
        </Button>
      </div>
    </div>
  );
}
